import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, Grid, TextField, Button, Checkbox, FormControlLabel,
  Stack, Alert, MenuItem, Divider
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import {
  createPaymentMethod,
  getPaymentMethodById,
  updatePaymentMethod,
  getAllPixIntegrations,
} from '../services/api';
import { getApiErrorMessage } from '../utils/apiError';

const zen = {
  blue: '#1976d2',
  blueDark: '#155fa0',
  sectionTitleSx: {
    background: '#1976d2',
    color: '#fff',
    fontWeight: 800,
    letterSpacing: 0.3,
    px: 2,
    py: 1,
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
  },
  sectionCardSx: {
    borderRadius: 2,
    border: '1px solid rgba(0,0,0,0.08)',
    boxShadow: '0 2px 10px rgba(0,0,0,0.04)',
    backgroundColor: 'background.paper',
  },
};

function Section({ title, children, sx }) {
  return (
    <Box sx={{ ...zen.sectionCardSx, ...sx }}>
      <Box sx={zen.sectionTitleSx}>{title}</Box>
      <Box sx={{ p: 2 }}>{children}</Box>
    </Box>
  );
}

const TPAG_LIST = [
  { value: 1, label: '01 - Dinheiro' },
  { value: 2, label: '02 - Cheque' },
  { value: 3, label: '03 - Cartão de Crédito' },
  { value: 4, label: '04 - Cartão de Débito' },
  { value: 5, label: '05 - Crédito Loja' },
  { value: 10, label: '10 - Vale Alimentação' },
  { value: 11, label: '11 - Vale Refeição' },
  { value: 12, label: '12 - Vale Presente' },
  { value: 13, label: '13 - Vale Combustível' },
  { value: 15, label: '15 - Boleto Bancário' },
  { value: 16, label: '16 - Depósito Bancário' },
  { value: 17, label: '17 - Pagamento Instantâneo (PIX)' },
  { value: 18, label: '18 - Transferência bancária' },
  { value: 19, label: '19 - Programa de fidelidade' },
  { value: 90, label: '90 - Sem pagamento' },
  { value: 99, label: '99 - Outros' },
];

export default function FormPaymentMethod({
  embedded = false,
  paymentMethodId = null,
  onSaved,
  onCancel,
}) {
  const params = useParams();
  const id = paymentMethodId ?? params.id;
  const isEditMode = !!id;
  const navigate = useNavigate();

  const [form, setForm] = useState({
    id: 0,
    descricao: '',
    referenciaSat: 1,
    observacao: '',
    ativo: true,
    troco: false,
    repique: false,
    troca: false,
    convenio: false,
    pix: false,
    chavePix: '',
    tef: false,
    pixIntegrado: false,
    integracaoPixId: '',
    autoAtendimento: true,
    exclusivaNfe: false,
  });
  const [pixIntegracoes, setPixIntegracoes] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [saving, setSaving] = useState(false);

  const titulo = useMemo(
    () => (isEditMode ? `Editar Forma de Pagamento #${id}` : 'Nova Forma de Pagamento'),
    [isEditMode, id]
  );

  useEffect(() => {
    (async () => {
      try {
        const { data } = await getAllPixIntegrations();
        setPixIntegracoes(Array.isArray(data) ? data.filter((p) => p.ativo !== false) : []);
      } catch {
        /* lista opcional */
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (!isEditMode) return;
      try {
        const { data } = await getPaymentMethodById(id);
        setForm({
          id: data.id,
          descricao: data.descricao || '',
          referenciaSat: Number(data.referenciaSat ?? 1),
          observacao: data.observacao || '',
          ativo: data.ativo !== false,
          troco: !!data.troco,
          repique: !!data.repique,
          troca: !!data.troca,
          convenio: !!data.convenio,
          pix: !!data.pix,
          chavePix: data.chavePix || '',
          tef: !!data.tef,
          pixIntegrado: !!data.pixIntegrado,
          integracaoPixId: data.integracaoPixId ?? '',
          autoAtendimento: data.autoAtendimento !== false,
          exclusivaNfe: !!data.exclusivaNfe,
        });
      } catch (err) {
        setErrorMessage(getApiErrorMessage(err, 'Não foi possível carregar a forma de pagamento.'));
      }
    })();
  }, [isEditMode, id]);

  const onChange = (field) => (e) => {
    const isCheckbox = e.target.type === 'checkbox';
    const val = isCheckbox ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: val }));
  };

  const fechar = () => {
    if (onCancel) onCancel();
    else navigate('/payment-methods');
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setSaving(true);
    try {
      if (!form.descricao.trim()) throw new Error('Descrição é obrigatória.');
      if (form.pixIntegrado && !form.integracaoPixId) {
        throw new Error('Selecione a integração PIX.');
      }

      const payload = {
        id: isEditMode ? Number(id) : 0,
        descricao: form.descricao.trim(),
        referenciaSat: Number(form.referenciaSat),
        observacao: form.observacao || null,
        ativo: !!form.ativo,
        troco: !!form.troco,
        repique: !!form.repique,
        troca: !!form.troca,
        convenio: !!form.convenio,
        pix: !!form.pix,
        chavePix: form.chavePix || null,
        tef: !!form.tef,
        pixIntegrado: !!form.pixIntegrado,
        integracaoPixId: form.pixIntegrado && form.integracaoPixId
          ? Number(form.integracaoPixId)
          : null,
        autoAtendimento: !!form.autoAtendimento,
        exclusivaNfe: !!form.exclusivaNfe,
      };

      if (isEditMode) await updatePaymentMethod(Number(id), payload);
      else await createPaymentMethod(payload);

      if (onSaved) {
        onSaved(isEditMode ? 'Forma de pagamento atualizada' : 'Forma de pagamento criada');
      } else {
        navigate('/payment-methods');
      }
    } catch (err) {
      setErrorMessage(getApiErrorMessage(err, 'Erro ao salvar a forma de pagamento.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box component="form" onSubmit={onSubmit} sx={{ p: embedded ? 0 : 3, maxWidth: embedded ? 'none' : 1000, mx: 'auto' }}>
      {!embedded && (
        <Typography variant="h5" sx={{ textAlign: 'center', color: zen.blue, fontWeight: 900, mb: 2 }}>
          {titulo}
        </Typography>
      )}

      {errorMessage && <Alert severity="error" sx={{ mb: 2 }}>{String(errorMessage)}</Alert>}

      <Section title="Identificação" sx={{ mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField label="Descrição" value={form.descricao} onChange={onChange('descricao')} fullWidth required />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Código fiscal de pagamento"
              select
              value={form.referenciaSat}
              onChange={onChange('referenciaSat')}
              fullWidth
              required
            >
              {TPAG_LIST.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Observação"
              value={form.observacao}
              onChange={onChange('observacao')}
              fullWidth
              multiline
              minRows={2}
            />
          </Grid>
          <Grid item xs={12}>
            <Stack direction="row" spacing={2} flexWrap="wrap">
              <FormControlLabel control={<Checkbox checked={form.ativo} onChange={onChange('ativo')} />} label="Ativo" />
              <FormControlLabel control={<Checkbox checked={form.autoAtendimento} onChange={onChange('autoAtendimento')} />} label="Autoatendimento" />
              <FormControlLabel control={<Checkbox checked={form.exclusivaNfe} onChange={onChange('exclusivaNfe')} />} label="Exclusiva NF-e" />
            </Stack>
          </Grid>
        </Grid>
      </Section>

      <Section title="Comportamento no caixa" sx={{ mb: 2 }}>
        <Stack direction="row" spacing={2} flexWrap="wrap">
          <FormControlLabel control={<Checkbox checked={form.troco} onChange={onChange('troco')} />} label="Permite troco" />
          <FormControlLabel control={<Checkbox checked={form.repique} onChange={onChange('repique')} />} label="Repique" />
          <FormControlLabel control={<Checkbox checked={form.troca} onChange={onChange('troca')} />} label="Troca" />
          <FormControlLabel control={<Checkbox checked={form.convenio} onChange={onChange('convenio')} />} label="Convênio" />
          <FormControlLabel control={<Checkbox checked={form.tef} onChange={onChange('tef')} />} label="TEF" />
        </Stack>
      </Section>

      <Section title="PIX" sx={{ mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Stack direction="row" spacing={2} flexWrap="wrap">
              <FormControlLabel control={<Checkbox checked={form.pix} onChange={onChange('pix')} />} label="PIX (manual/estático)" />
              <FormControlLabel control={<Checkbox checked={form.pixIntegrado} onChange={onChange('pixIntegrado')} />} label="PIX integrado (API)" />
            </Stack>
          </Grid>
          {form.pix && !form.pixIntegrado && (
            <Grid item xs={12} md={8}>
              <TextField
                label="Chave PIX"
                value={form.chavePix}
                onChange={onChange('chavePix')}
                fullWidth
              />
            </Grid>
          )}
          {form.pixIntegrado && (
            <Grid item xs={12} md={8}>
              <TextField
                label="Integração PIX"
                select
                value={form.integracaoPixId}
                onChange={onChange('integracaoPixId')}
                fullWidth
                required
                helperText={pixIntegracoes.length === 0 ? 'Cadastre uma integração PIX antes.' : ' '}
              >
                {pixIntegracoes.map((p) => (
                  <MenuItem key={p.id} value={p.id}>{p.descricao}</MenuItem>
                ))}
              </TextField>
            </Grid>
          )}
        </Grid>
      </Section>

      <Divider sx={{ my: 2 }} />

      <Stack direction="row" spacing={1} justifyContent="center">
        <Button
          type="submit"
          variant="contained"
          disabled={saving}
          startIcon={<SaveIcon />}
          sx={{ minWidth: 160, background: zen.blue, '&:hover': { background: zen.blueDark }, textTransform: 'none', fontWeight: 700 }}
        >
          {isEditMode ? 'Salvar Alterações' : 'Cadastrar'}
        </Button>
        <Button
          variant="contained"
          color="inherit"
          onClick={fechar}
          startIcon={<CancelIcon />}
          sx={{ minWidth: 160, textTransform: 'none', fontWeight: 700 }}
        >
          Cancelar
        </Button>
      </Stack>
    </Box>
  );
}
